import { useEffect, useRef } from "react";

import type { OrganizationUser } from "@/Dashboard/pages/Organizations/OrganizationUsers/types";
import { useThunkDispatch } from "@/store/hooks";
import { fetchOrganizationUsers } from "@/store/v2/organizationUsers/organizationUsersSlice";
import type { Query } from "@/store/v2/queries/types";

interface UseOrganizationUsersData {
  users?: OrganizationUser[];
  offset?: number;
  limit?: number;
  sortField?: string;
  sortOrientation?: string;
  searchText?: string;
  hookEnabled?: boolean;
  organizationId: number | null;
  skipInitialFetch?: boolean; // Skip the initial fetch if true, useful for avoiding unnecessary fetches if the data loaded in loader
}

export function useOrganizationUsersData(props: UseOrganizationUsersData) {
  const {
    users = [],
    offset,
    limit,
    sortField,
    sortOrientation,
    searchText,
    hookEnabled = true,
    organizationId,
    skipInitialFetch = false,
  } = props;

  const dispatch = useThunkDispatch();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialRender = useRef(true);

  const fetchUsers = () => {
    if (!organizationId) return;

    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      const query: Query = {
        ...(sortField && { sortField }),
        ...(sortOrientation && { sortOrientation: sortOrientation.toUpperCase() as "asc" | "desc" }),
      };

      if (limit) {
        query.offset = users.length ? offset : 0;
        query.limit = limit;
      }

      if (searchText && searchText.length > 2) {
        query.searchText = searchText.trim().toLowerCase();
      }

      dispatch(fetchOrganizationUsers({ organizationId: organizationId!, query }));
    }, 300);
  };

  useEffect(() => {
    if (hookEnabled) {
      if (isInitialRender.current && skipInitialFetch) {
        isInitialRender.current = false;
        return;
      }

      isInitialRender.current = false;
      fetchUsers();
    }
  }, [sortField, sortOrientation, offset, limit, searchText, hookEnabled, skipInitialFetch]);
}
